/* WobbleBag product gallery.

   Defines the `media-gallery` custom element. Dawn's media-gallery.js guards its
   own definition with `if (!customElements.get('media-gallery'))`, so loading this
   file first replaces that behaviour while keeping the element name product-info.js
   looks for, along with its setActiveMedia(mediaId, prepend) contract. */
(function () {
  'use strict';

  if (customElements.get('media-gallery')) return;

  customElements.define(
    'media-gallery',
    class WobbleBagGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.viewer = this.querySelector('[id^="GalleryViewer"]');
        this.rail = this.querySelector('[id^="GalleryThumbnails"]');
        this.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.counter = this.querySelector('[data-gallery-current]');
        if (!this.viewer) return;

        this.bindThumbnails();

        this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.bindSwipe();
        this.updateCounter();
      }

      get items() {
        return Array.from(this.viewer.querySelectorAll('[data-media-id]'));
      }

      get activeItem() {
        return this.viewer.querySelector('[data-media-id].is-active') || this.items[0];
      }

      bindThumbnails() {
        if (!this.rail) return;
        this.rail.querySelectorAll('[data-target]').forEach((thumb) => {
          var button = thumb.querySelector('button');
          if (!button || button.dataset.bound === 'true') return;
          button.dataset.bound = 'true';
          button.addEventListener('click', () => this.setActiveMedia(thumb.dataset.target, false));
        });
      }

      onKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        // Leave arrow keys to form fields and to the zoom modal.
        var tag = event.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      }

      bindSwipe() {
        var startX = null;
        var startY = null;

        this.viewer.addEventListener(
          'touchstart',
          function (event) {
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
          },
          { passive: true }
        );

        this.viewer.addEventListener(
          'touchend',
          function (event) {
            if (startX === null) return;
            var dx = event.changedTouches[0].clientX - startX;
            var dy = event.changedTouches[0].clientY - startY;
            startX = null;
            // Horizontal intent only, so vertical scrolling still works.
            if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
            this.step(dx < 0 ? 1 : -1);
          }.bind(this),
          { passive: true }
        );
      }

      step(direction) {
        var items = this.items;
        if (items.length < 2) return;
        var index = items.indexOf(this.activeItem);
        var next = (index + direction + items.length) % items.length;
        this.setActiveMedia(items[next].dataset.mediaId, false);
      }

      /* Called by product-info.js when the selected variant changes. */
      setActiveMedia(mediaId, prepend) {
        var active =
          this.viewer.querySelector('[data-media-id="' + mediaId + '"]') ||
          this.viewer.querySelector('[data-media-id]');
        if (!active) return;

        this.items.forEach(function (item) {
          item.classList.remove('is-active');
        });
        active.classList.add('is-active');

        if (prepend && active.parentElement.firstChild !== active) {
          active.parentElement.prepend(active);
          var thumb = this.rail && this.rail.querySelector('[data-target="' + mediaId + '"]');
          if (thumb && thumb.parentElement.firstChild !== thumb) thumb.parentElement.prepend(thumb);
          this.bindThumbnails();
        }

        this.setActiveThumbnail(mediaId);
        this.updateCounter();
        this.playActiveMedia(active);
        this.announce(active);
      }

      setActiveThumbnail(mediaId) {
        if (!this.rail) return;
        var active = null;
        this.rail.querySelectorAll('[data-target]').forEach(function (thumb) {
          var button = thumb.querySelector('button');
          if (!button) return;
          if (thumb.dataset.target === mediaId) {
            button.setAttribute('aria-current', 'true');
            active = thumb;
          } else {
            button.removeAttribute('aria-current');
          }
        });
        if (!active) return;

        // Keep the active thumbnail in view in whichever axis the rail scrolls.
        var railBox = this.rail.getBoundingClientRect();
        var thumbBox = active.getBoundingClientRect();
        var vertical = this.rail.scrollHeight > this.rail.clientHeight + 1;
        if (vertical) {
          if (thumbBox.top < railBox.top || thumbBox.bottom > railBox.bottom) {
            this.rail.scrollTo({ top: active.offsetTop - this.rail.clientHeight / 2 + active.clientHeight / 2 });
          }
        } else if (thumbBox.left < railBox.left || thumbBox.right > railBox.right) {
          this.rail.scrollTo({ left: active.offsetLeft - this.rail.clientWidth / 2 + active.clientWidth / 2 });
        }
      }

      updateCounter() {
        if (!this.counter) return;
        var index = this.items.indexOf(this.activeItem);
        this.counter.textContent = index < 0 ? 1 : index + 1;
      }

      playActiveMedia(active) {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        var deferred = active.querySelector('.deferred-media');
        // Only autoload video once the shopper picks it, never on first paint.
        if (deferred && deferred.getAttribute('loaded') && typeof deferred.loadContent === 'function') {
          deferred.loadContent(false);
        }
      }

      announce(active) {
        if (!this.liveRegion || !window.accessibilityStrings) return;
        var position = active.dataset.mediaPosition || '';
        this.liveRegion.setAttribute('aria-hidden', 'false');
        this.liveRegion.textContent = (window.accessibilityStrings.imageAvailable || '[index]').replace(
          '[index]',
          position
        );
        var region = this.liveRegion;
        setTimeout(function () {
          region.setAttribute('aria-hidden', 'true');
        }, 2000);
      }
    }
  );
})();
